import $axios from '../libs/OAuth2/services/axios';
import md5 from 'md5';
import { cacheCall } from '../libs/Api/services/api';

const emailToMd5 = email => md5(email.toString().toLowerCase());
const defaultPerPage = 15;

export default {
  // Dictionary
  getGenders: () => Promise.resolve({
    data: [
      { id: 'M', label: 'Maschio' },
      { id: 'F', label: 'Femmina' },
    ],
  }),

  // Uploader
  uploadFile: file => $axios.post('/temporaryFiles', { file }),

  // Geo
  autocompleteTowns: (search, cancelToken) => $axios.get('/geo/towns/autocomplete?search=' + encodeURI(search), { cancelToken }),

  // Registration
  registerUser: user => $axios.post('/users', user),
  activateUser: (email, token) => $axios.post(`/users/${email}/activation`, { token }),

  // Change password
  getChangePasswordToken: email => $axios.post(`/users/${emailToMd5(email)}/changePasswordTokens`),
  changePasswordWithToken: (email, token, password) => $axios.post(`/users/${email}/password`, { token, password }),

  // My profile
  getMyProfile: () => $axios.get('/profile'),
  updateMyProfile: user => $axios.put('/profile', user),
  changeMyPassword: (password, newPassword) => $axios.post('/profile/password', { password, newPassword }),
  listMyClubsAsCoach: () => $axios.get('/profile/clubs'),

  // Dashboard
  getCounters: () => $axios.get('/counters'),

  // Clubs
  listClubs: (page = 1, perPage = defaultPerPage, params = {}) => $axios.get('/clubs', {
    params: {
      ...params,
      page,
      perPage,
    },
  }),
  getListClubsCsv: (params = {}) => $axios.get('/clubs.csv', {
    params: params,
  }),
  createClub: club => $axios.post('/clubs', club),
  fetchClub: id => $axios.get('/clubs/' + id),
  removeClub: id => $axios.delete('/clubs/' + id),
  updateClub: (id, club) => $axios.put('/clubs/' + id, club),
  autocompleteClubs: (search, cancelToken) => $axios.get('/clubs/autocomplete?search=' + encodeURI(search), { cancelToken }),
  getClubSettings: id => $axios.get('/clubs/' + id + '/settings'),
  getClubCoaches: id => $axios.get('/clubs/' + id + '/coaches'),
  removeCoachFromClub: (idCoach, idClub) => $axios.delete('/clubs/' + idClub + '/coaches/' + idCoach),
  addCoachToClub: (idCoach, idClub) => $axios.post('/clubs/' + idClub + '/coaches', {
    coach: idCoach,
  }),

  // Coaches
  listCoaches: (page = 1, perPage = defaultPerPage, params = {}) => $axios.get('/coaches', {
    params: {
      ...params,
      page,
      perPage,
    },
  }),
  getListCoachesCsv: (params = {}) => $axios.get('/coaches.csv', {
    params: params,
  }),
  createCoach: coach => $axios.post('/coaches', coach),
  fetchCoach: id => $axios.get('/coaches/' + id),
  removeCoach: id => $axios.delete('/coaches/' + id),
  updateCoach: (id, coach) => $axios.put('/coaches/' + id, coach),
  autocompleteCoach: (search, cancelToken) => $axios.get('/coaches/autocomplete?search=' + encodeURI(search), { cancelToken }),

  // Parents
  listParents: (page = 1, perPage = defaultPerPage, params = {}) => $axios.get('/parents', {
    params: {
      ...params,
      page,
      perPage,
    },
  }),
  getListParentsCsv: (params = {}) => $axios.get('/parents.csv', {
    params: params,
  }),
  createParent: parent => $axios.post('/parents', parent),
  fetchParent: id => $axios.get('/parents/' + id),
  removeParent: id => $axios.delete('/parents/' + id),
  updateParent: (id, parent) => $axios.put('/parents/' + id, parent),
  autocompleteParents: (search, cancelToken) => $axios.get('/parents/autocomplete?search=' + encodeURI(search), { cancelToken }),
  forceParentActivation: id => $axios.post(`/parents/${id}/activation`),
  resendParentActivationEmail: id => $axios.post(`/parents/${id}/activation-email`),

  // Players
  listPlayers: (page = 1, perPage = defaultPerPage, params = {}) => $axios.get('/players', {
    params: {
      ...params,
      page,
      perPage,
    },
  }),
  getListPlayersCsv: (params = {}) => $axios.get('/players.csv', {
    params: params,
  }),
  createPlayer: player => $axios.post('/players', player),
  fetchPlayer: id => $axios.get('/players/' + id),
  removePlayer: id => $axios.delete('/players/' + id),
  updatePlayer: (id, player) => $axios.put('/players/' + id, player),
  fetchYearAffiliations: id => $axios.get('/players/' + id + '/year-affiliations'),
  setYearAffiliationPaid: (id, year) => $axios.post('/players/' + id + '/year-affiliations/' + year),
  autocompletePlayers: (search, cancelToken) => $axios.get('/players/autocomplete?search=' + encodeURI(search), { cancelToken }),
  listMyPlayerCompetitions: (when = 'past') => $axios.get('/players/competitions', {
    params: {
      when,
    },
  }),

  // Competitions
  listCategories: () => cacheCall('listCategories', () => $axios.get('/categories')),
  listRegions: () => cacheCall('listRegions', () => $axios.get('/regions')),
  fetchRegion: id => $axios.get('/regions/' + id),
  listZones: () => $axios.get('/regions'),
  fetchZone: id => $axios.get('/zones/' + id),
  listCompetitionsWithParams: (params = {}) => $axios.get('/competitions', { params }),
  listCompetitions (page = 1, perPage = defaultPerPage, params = {}) {
    return this.listCompetitionsWithParams({
      ...params,
      page,
      perPage,
    });
  },
  listAllCompetitions (params = {}) {
    return this.listCompetitionsWithParams({
      ...params,
      all: true,
    });
  },
  listCompetitionsByRegion (regionId, page = 1, perPage = defaultPerPage, params = {}) {
    return this.listCompetitions(page, perPage, { ...params, region: regionId });
  },
  listCompetitionsByZone (zoneId, page = 1, perPage = defaultPerPage, params = {}) {
    return this.listCompetitions(page, perPage, { ...params, zone: zoneId });
  },
  fetchCompetition: id => $axios.get('/competitions/' + id),
  createCompetition: competition => $axios.post('/competitions', competition),
  removeCompetition: id => $axios.delete('/competitions/' + id),
  updateCompetition: (id, competition) => $axios.put('/competitions/' + id, competition),
  publishCompetition: id => $axios.post('/competitions/' + id + '/pubblications'),
  setTeeTimesForCompetition: (id, teeTimes, completed = false) => $axios.post('/competitions/' + id + '/tee-times', {
    teeTimes,
    completed,
  }),
  setResultsForCompetition: (id, scores, completed = false) => $axios.post('/competitions/' + id + '/results', {
    scores,
    completed,
  }),
  addPlayerToCompetition: (id, idPlayer, paid = false) => $axios.post('/competitions/' + id + '/players', {
    idPlayer,
    paid,
  }),
  removePlayerFromCompetition: (id, idPlayer) => $axios.delete('/competitions/' + id + '/players/' + idPlayer),
  fetchPlayersForCompetition: id => $axios.get('/competitions/' + id + '/players'),
  removePlayerSubscription: id => $axios.delete('/competitions/subscriptions/' + id),
  fetchPlayerCompetitionSubscriptionInfo: (id, idPlayer) => $axios.get(`/competitions/${id}/players/${idPlayer}`),

  // Payments
  listPayments: (page = 1, perPage = defaultPerPage, params = {}) => $axios.get('/payments', {
    params: {
      ...params,
      page,
      perPage,
    },
  }),
  fetchPayment: id => $axios.get('/payments/' + id),
  fetchCart: () => $axios.get('/payments/cart'),
  createPayment: () => $axios.post('/payments'),
  finalizePayment: sessionId => $axios.post('/payments/finalize', {
    sessionId,
  }),

  // Settings
  fetchSettings: () => $axios.get('/settings'),
  updateSettings: (settings) => $axios.put('/settings', { settings }),
};
